import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Table, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Generate list of months
const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Generate years (last 10 years)
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => currentYear - i + 1);

// Machine groups for the pivot table filter
const MACHINE_GROUPS = [
  { value: 'all', label: 'All Groups' },
  { value: 'Nut Tapping', label: 'Nut Tapping' },
  { value: 'Nut Forming', label: 'Nut Forming' },
  { value: 'Bolt Threading', label: 'Bolt Threading' },
  { value: 'Bolt Forging', label: 'Bolt Forging' },
  { value: 'Stud Threading', label: 'Stud Threading' },
  { value: 'Wire Draw', label: 'Wire Draw' },
];

export const MachineMonthlyTable = ({ machineGroups, selectedGroups }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ machines: [], months: [] });
  const [availableMachines, setAvailableMachines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [tempSelectedMachines, setTempSelectedMachines] = useState([]); // Temporary selection for OK/Cancel
  const [showMachineFilter, setShowMachineFilter] = useState(false);
  
  // Machine Group filter for this table (independent of global filter)
  const [tableSelectedGroups, setTableSelectedGroups] = useState(['all']);
  const [tempSelectedGroups, setTempSelectedGroups] = useState(['all']);
  const [showGroupFilter, setShowGroupFilter] = useState(false);
  
  // Start and End month/year selection
  const [startMonth, setStartMonth] = useState('04'); // April
  const [startYear, setStartYear] = useState(currentYear.toString());
  const [endMonth, setEndMonth] = useState(new Date().getMonth() + 1 < 10 ? `0${new Date().getMonth() + 1}` : `${new Date().getMonth() + 1}`);
  const [endYear, setEndYear] = useState(currentYear.toString());

  // Generate months between start and end
  const getMonthsInRange = () => {
    const months = [];
    let current = new Date(parseInt(startYear), parseInt(startMonth) - 1, 1);
    const end = new Date(parseInt(endYear), parseInt(endMonth) - 1, 1);
    
    while (current <= end) {
      const year = current.getFullYear();
      const month = (current.getMonth() + 1).toString().padStart(2, '0');
      months.push(`${year}-${month}`);
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  };

  // Fetch machines based on table's group filter
  useEffect(() => {
    const fetchMachines = async () => {
      try {
        const params = {};
        if (!tableSelectedGroups.includes('all')) {
          params.machine_groups = tableSelectedGroups.join(',');
        }
        const response = await axios.get(`${API}/machines`, { params });
        setAvailableMachines(response.data);
        // Reset machine selection when groups change
        setSelectedMachines([]);
        setTempSelectedMachines([]);
      } catch (error) {
        console.error('Error fetching machines:', error);
      }
    };
    fetchMachines();
  }, [tableSelectedGroups]);

  // Fetch data when filters change
  useEffect(() => {
    const fetchData = async () => {
      const monthsInRange = getMonthsInRange();
      if (monthsInRange.length === 0) return;
      
      setLoading(true);
      try {
        const params = {
          months: monthsInRange.join(',')
        };
        
        // Use table's own group filter
        if (!tableSelectedGroups.includes('all')) {
          params.machine_groups = tableSelectedGroups.join(',');
        }
        
        if (selectedMachines.length > 0) {
          params.machines = selectedMachines.join(',');
        }
        
        const response = await axios.get(`${API}/production/machine-monthly`, { params });
        setData(response.data);
      } catch (error) {
        console.error('Error fetching machine monthly data:', error);
        toast.error('Failed to load machine monthly data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [startMonth, startYear, endMonth, endYear, selectedMachines, tableSelectedGroups]);

  const handleMachineToggle = (machine) => {
    setSelectedMachines(prev => 
      prev.includes(machine) 
        ? prev.filter(m => m !== machine)
        : [...prev, machine]
    );
  };

  const handleTempMachineToggle = (machine) => {
    setTempSelectedMachines(prev => 
      prev.includes(machine) 
        ? prev.filter(m => m !== machine)
        : [...prev, machine]
    );
  };

  const handleApplyMachineFilter = () => {
    setSelectedMachines([...tempSelectedMachines]);
    setShowMachineFilter(false);
  };

  const handleCancelMachineFilter = () => {
    setTempSelectedMachines([...selectedMachines]);
    setShowMachineFilter(false);
  };

  const selectAllMachines = () => setSelectedMachines([]);

  // Machine Group filter handlers
  const handleTempGroupToggle = (group) => {
    if (group === 'all') {
      setTempSelectedGroups(['all']);
    } else {
      setTempSelectedGroups(prev => {
        const withoutAll = prev.filter(g => g !== 'all');
        if (prev.includes(group)) {
          const newGroups = withoutAll.filter(g => g !== group);
          return newGroups.length === 0 ? ['all'] : newGroups;
        } else {
          return [...withoutAll, group];
        }
      });
    }
  };

  const handleApplyGroupFilter = () => {
    setTableSelectedGroups([...tempSelectedGroups]);
    setShowGroupFilter(false);
  };

  const handleCancelGroupFilter = () => {
    setTempSelectedGroups([...tableSelectedGroups]);
    setShowGroupFilter(false);
  };

  const getGroupFilterLabel = () => {
    if (tableSelectedGroups.includes('all')) return 'All Groups';
    return `${tableSelectedGroups.length} Group${tableSelectedGroups.length > 1 ? 's' : ''}`;
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (data.machines.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Build CSV content
    const headers = ['Machine', 'Group', ...data.months.map(m => formatMonth(m)), 'Total'];
    const rows = data.machines.map(machine => {
      const rowTotal = data.months.reduce((sum, month) => sum + (machine.monthly_data[month] || 0), 0);
      return [
        machine.machine,
        machine.machine_group,
        ...data.months.map(month => {
          const value = machine.monthly_data[month];
          return value ? Math.round(value / 1000) : 0;
        }),
        Math.round(rowTotal / 1000)
      ];
    });

    // Add totals row
    const columnTotals = data.months.map(month => {
      const total = data.machines.reduce((sum, m) => sum + (m.monthly_data[month] || 0), 0);
      return Math.round(total / 1000);
    });
    const grandTotal = data.machines.reduce((sum, m) => {
      return sum + data.months.reduce((s, month) => s + (m.monthly_data[month] || 0), 0);
    }, 0);
    rows.push(['Total', '', ...columnTotals, Math.round(grandTotal / 1000)]);

    // Create CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Generate filename with date range
    const fromMonth = MONTHS.find(m => m.value === startMonth)?.label || startMonth;
    const toMonth = MONTHS.find(m => m.value === endMonth)?.label || endMonth;
    const filename = `Machine_Monthly_Production_${fromMonth}_${startYear}_to_${toMonth}_${endYear}.csv`;
    link.setAttribute('download', filename);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('CSV exported successfully');
  };

  const formatMonth = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const formatMonthShort = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} '${year.slice(2)}`;
  };

  const formatValue = (value) => {
    if (!value) return '-';
    const mt = Math.round(value / 1000);
    return mt.toLocaleString();
  };

  // Calculate totals
  const calculateRowTotal = (machine) => {
    return data.months.reduce((sum, month) => {
      return sum + (machine.monthly_data[month] || 0);
    }, 0);
  };

  const calculateColumnTotal = (month) => {
    return data.machines.reduce((sum, machine) => {
      return sum + (machine.monthly_data[month] || 0);
    }, 0);
  };

  const grandTotal = data.machines.reduce((sum, machine) => sum + calculateRowTotal(machine), 0);

  return (
    <Card className="rounded-sm border-border shadow-sm" data-testid="machine-monthly-table">
      <CardHeader className="border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-bold uppercase tracking-tight flex items-center gap-2">
              <Table className="h-5 w-5" />
              Machine-wise Monthly Production (MT)
            </CardTitle>
            <p className="text-xs text-muted-foreground uppercase tracking-widest mt-1">
              Select date range and machines to view detailed breakdown
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToCSV}
            disabled={data.machines.length === 0}
            className="rounded-sm flex items-center gap-2"
            data-testid="export-csv-button"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-4 mb-4">
          {/* Start Month/Year */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">From</label>
            <div className="flex gap-2">
              <Select value={startMonth} onValueChange={setStartMonth}>
                <SelectTrigger className="w-[120px] rounded-sm" data-testid="start-month-select">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(month => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={startYear} onValueChange={setStartYear}>
                <SelectTrigger className="w-[90px] rounded-sm" data-testid="start-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* End Month/Year */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">To</label>
            <div className="flex gap-2">
              <Select value={endMonth} onValueChange={setEndMonth}>
                <SelectTrigger className="w-[120px] rounded-sm" data-testid="end-month-select">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(month => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={endYear} onValueChange={setEndYear}>
                <SelectTrigger className="w-[90px] rounded-sm" data-testid="end-year-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Machine Group Filter */}
          <div className="relative">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block mb-1">Machine Group</label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!showGroupFilter) {
                  setTempSelectedGroups([...tableSelectedGroups]);
                }
                setShowGroupFilter(!showGroupFilter);
              }}
              className="rounded-sm min-w-[150px] justify-between"
              data-testid="group-filter-button"
            >
              <span>{getGroupFilterLabel()}</span>
              {showGroupFilter ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
            </Button>
            {showGroupFilter && (
              <div className="absolute z-50 mt-1 bg-card border border-border rounded-sm shadow-lg p-3 min-w-[220px]">
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {MACHINE_GROUPS.map(group => (
                    <label key={group.value} className="flex items-center gap-2 cursor-pointer hover:bg-accent p-1 rounded">
                      <Checkbox
                        checked={group.value === 'all' ? tempSelectedGroups.includes('all') : tempSelectedGroups.includes(group.value)}
                        onCheckedChange={() => handleTempGroupToggle(group.value)}
                      />
                      <span className="text-sm">{group.label}</span>
                    </label>
                  ))}
                </div>
                {/* OK and Cancel buttons */}
                <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelGroupFilter}
                    className="text-xs"
                    data-testid="group-filter-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleApplyGroupFilter}
                    className="text-xs"
                    data-testid="group-filter-ok"
                  >
                    OK
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Machine Filter */}
          <div className="relative">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block mb-1">Machines</label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!showMachineFilter) {
                  setTempSelectedMachines([...selectedMachines]);
                }
                setShowMachineFilter(!showMachineFilter);
              }}
              className="rounded-sm min-w-[150px] justify-between"
              data-testid="machine-filter-button"
            >
              <span>{selectedMachines.length === 0 ? 'All Machines' : `${selectedMachines.length} Selected`}</span>
              {showMachineFilter ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
            </Button>
            {showMachineFilter && (
              <div className="absolute z-50 mt-1 bg-card border border-border rounded-sm shadow-lg p-3 min-w-[280px]">
                <div className="flex gap-2 mb-2 border-b pb-2">
                  <Button size="sm" variant="ghost" onClick={() => setTempSelectedMachines([])} className="text-xs">All Machines</Button>
                </div>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {availableMachines.map(machine => (
                    <label key={machine} className="flex items-center gap-2 cursor-pointer hover:bg-accent p-1 rounded">
                      <Checkbox
                        checked={tempSelectedMachines.includes(machine)}
                        onCheckedChange={() => handleTempMachineToggle(machine)}
                      />
                      <span className="text-sm">{machine}</span>
                    </label>
                  ))}
                </div>
                {/* OK and Cancel buttons */}
                <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCancelMachineFilter}
                    className="text-xs"
                    data-testid="machine-filter-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleApplyMachineFilter}
                    className="text-xs"
                    data-testid="machine-filter-ok"
                  >
                    OK
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : data.machines.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No data available for selected range
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-muted">
                  <th className="border border-border p-2 text-left font-semibold sticky left-0 bg-muted z-10">Machine</th>
                  <th className="border border-border p-2 text-left font-semibold">Group</th>
                  {data.months.map(month => (
                    <th key={month} className="border border-border p-2 text-right font-semibold min-w-[70px]">
                      {formatMonthShort(month)}
                    </th>
                  ))}
                  <th className="border border-border p-2 text-right font-semibold bg-muted min-w-[80px]">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.machines.map((machine, idx) => (
                  <tr key={machine.machine} className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/30'}>
                    <td className="border border-border p-2 font-medium sticky left-0 bg-inherit z-10">{machine.machine}</td>
                    <td className="border border-border p-2 text-muted-foreground">{machine.machine_group}</td>
                    {data.months.map(month => (
                      <td key={month} className="border border-border p-2 text-right">
                        {formatValue(machine.monthly_data[month])}
                      </td>
                    ))}
                    <td className="border border-border p-2 text-right font-semibold bg-muted/50">
                      {formatValue(calculateRowTotal(machine))}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted font-semibold">
                  <td className="border border-border p-2 sticky left-0 bg-muted z-10">Total</td>
                  <td className="border border-border p-2"></td>
                  {data.months.map(month => (
                    <td key={month} className="border border-border p-2 text-right">
                      {formatValue(calculateColumnTotal(month))}
                    </td>
                  ))}
                  <td className="border border-border p-2 text-right bg-primary/10">
                    {formatValue(grandTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
